import type {
  CategoryConfig,
  MarkerCategory,
  ShapeCategory,
  ShapeKind,
} from "./types";

/** Built-in fallback used only if GET /api/config is unreachable. */
export const DEFAULT_CONFIG: CategoryConfig = {
  markerCategories: [
    { id: "muell", label: "Mülleimer", color: "#2f7a4d", shape: "circle", icon: "trash", imageUrl: null },
    { id: "aschenbecher", label: "Aschenbecher", color: "#8a5a1f", shape: "circle", icon: "ashtray", imageUrl: null },
    { id: "bank", label: "Sitzbank", color: "#2f5f8a", shape: "circle", icon: "bench", imageUrl: null },
    { id: "absperrung", label: "Absperrung / Poller", color: "#b23b2e", shape: "circle", icon: "barrier", imageUrl: null },
    { id: "schild", label: "Hinweisschild", color: "#9a8412", shape: "circle", icon: "sign", imageUrl: null },
    { id: "pflanze", label: "Pflanze", color: "#1c7a72", shape: "circle", icon: "plant", imageUrl: null },
    { id: "steckdose", label: "Steckdose", color: "#555b66", shape: "circle", icon: "plug", imageUrl: null },
    { id: "sonstiges", label: "Sonstiges", color: "#7a3f8a", shape: "circle", icon: "dot", imageUrl: null },
  ],
  shapeCategories: { area: [], object: [], line: [] },
};

/** Fallback colour per shape kind when a shape has no (matching) category. */
export const KIND_DEFAULT_COLOR: Record<ShapeKind, string> = {
  area: "#c8791f",
  object: "#5b4636",
  line: "#b23b2e",
};

export const KIND_LABEL: Record<ShapeKind, string> = {
  area: "Aktionsfläche",
  object: "Objektfläche",
  line: "Tensator-Absperrung",
};

export const KIND_LABEL_PLURAL: Record<ShapeKind, string> = {
  area: "Aktionsflächen",
  object: "Objektflächen",
  line: "Tensator-Absperrungen",
};

export const KIND_MIN_POINTS: Record<ShapeKind, number> = {
  area: 3,
  object: 3,
  // 1 = a single standalone tensator post (no belt); 2+ = posts joined by a belt.
  line: 1,
};

let active: CategoryConfig = DEFAULT_CONFIG;
const listeners = new Set<() => void>();

/** Subscribe to category changes (fired after loadConfig / reloadConfig). */
export function onConfigChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export async function loadConfig(): Promise<CategoryConfig> {
  try {
    const res = await fetch("/api/config");
    if (res.ok) {
      const data = (await res.json()) as CategoryConfig;
      if (Array.isArray(data.markerCategories) && data.shapeCategories) {
        active = data;
      }
    }
  } catch {
    /* keep whatever we have */
  }
  for (const fn of listeners) fn();
  return active;
}

export const reloadConfig = loadConfig;

/** Injects a config bundle fetched some other way (e.g. the public share view,
 * which gets it alongside the project state instead of via /api/config). */
export function setConfig(data: CategoryConfig): void {
  active = data;
  for (const fn of listeners) fn();
}

export function config(): CategoryConfig {
  return active;
}

export function markerCats(): MarkerCategory[] {
  return active.markerCategories;
}

export function shapeCats(kind: ShapeKind): ShapeCategory[] {
  return active.shapeCategories[kind] ?? DEFAULT_CONFIG.shapeCategories[kind];
}

/** All "Fläche" categories — the area/object distinction is not surfaced in the UI. */
export function flaecheCats(): ShapeCategory[] {
  return [...(active.shapeCategories.area ?? []), ...(active.shapeCategories.object ?? [])];
}

/** Categories that apply to a shape of the given kind (lines vs. everything else). */
export function catsForKind(kind: ShapeKind): ShapeCategory[] {
  return kind === "line" ? shapeCats("line") : flaecheCats();
}

const UNKNOWN_MARKER: MarkerCategory = {
  id: "",
  label: "Unbekannt",
  color: "#8b8471",
  shape: "circle",
  icon: "dot",
  imageUrl: null,
};

export function markerCatById(id: string): MarkerCategory {
  return active.markerCategories.find((c) => c.id === id) ?? UNKNOWN_MARKER;
}

export function shapeCatById(kind: ShapeKind, id: string): ShapeCategory {
  const list = catsForKind(kind);
  return (
    list.find((c) => c.id === id) ??
    { id: id || "", label: KIND_LABEL[kind], color: KIND_DEFAULT_COLOR[kind] }
  );
}
